import { firstValueFrom } from 'rxjs';
import type { CloneRecordsApiProps } from '../../@types/records';
import { constructRecordTitle } from '../../components/record/util/record-component-util';
import i18next from '../../i18n';
import { moduleApi } from '../../redux/services/module';
import { recordApi } from '../../redux/services/record';
import { appDispatch } from '../../redux/store-accessors';
import { getActiveRecordReference } from '../active-record.service';
import { getDataGridReference } from '../data-grid.service';
import { showSuccess } from '../notification.service';
import { BackgroundJobHandler } from './background-job.handler';

export class CloneRecordsBackgroundJobHandler extends BackgroundJobHandler<CloneRecordsApiProps> {
  constructor(apiProps: CloneRecordsApiProps, details?: Record<string, unknown>) {
    super(apiProps, details);
  }

  public createBackgroundJob() {
    return appDispatch(recordApi.endpoints.createRecordCloneJob.initiate(this.apiProps)).unwrap();
  }

  public async getJobDetails(backgroundJobId: number) {
    const subscription = appDispatch(
      recordApi.endpoints.getBackgroundJobById.initiate(backgroundJobId, { forceRefetch: true })
    );

    return subscription.unwrap().finally(() => subscription.unsubscribe());
  }

  public successCallback({ clonedRecordIds }: Record<string, string>): Promise<void> {
    appDispatch(recordApi.util.invalidateTags(['Record', 'TotalRecordCount', 'AssignedRecordsCount']));
    firstValueFrom(getDataGridReference()).then((dataGridRef) => {
      dataGridRef?.api.data.getDataOverwritePageCache();
    });

    if (!this.details?.hasMultipleRecords) {
      const getRecordByIdDispatch = appDispatch(
        recordApi.endpoints.getRecordById.initiate({ id: clonedRecordIds, keyTypeId: false })
      );
      const getModulesDispatch = appDispatch(moduleApi.endpoints.modules.initiate({}));

      return Promise.all([getRecordByIdDispatch.unwrap(), getModulesDispatch.unwrap()])
        .then(([recordResponse, modulesResponse]) => {
          const record = recordResponse.original;
          const title = constructRecordTitle(record.formName, record.docNo, record.status, record.title);
          const module = modulesResponse?.find((item) => item.viewId === record.viewId);
          firstValueFrom(getActiveRecordReference()).then((ref) => {
            ref?.addActiveRecord(String(record.id), module?.icon || '', title, undefined, {
              title,
              recordId: record.id,
              viewId: record.viewId,
              moduleIcon: module?.icon,
              formId: record.formId,
              formName: record.formName,
              docNo: record.docNo,
              status: record.status,
              vikingLinkUrl: record.vikingLinkUrl,
              displayText: record.displayText,
              openInEditMode: true,
            });
          });
        })
        .finally(() => {
          getRecordByIdDispatch.unsubscribe();
          getModulesDispatch.unsubscribe();
        });
    }

    showSuccess(i18next.t('performing-cloning-success'));

    return Promise.resolve();
  }
}
